import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useState, useCallback, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { AuthService } from '../services/AuthService';
// Provider component
export const AuthProvider = ({ config, children }) => {
    // Create auth service
    const service = useMemo(() => {
        return new AuthService(config);
    }, [config]);
    // State
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [user, setUser] = useState(null);
    const [tokens, setTokens] = useState({
        authorization: '',
        refresh: '',
    });
    const [deletedSession, setDeletedSession] = useState(undefined);
    const [sharedUser, setSharedUser] = useState(null);
    const [isSharedMode, setIsSharedMode] = useState(false);
    // Update auth state from response
    const updateAuthState = useCallback((response) => {
        setUser(response.user);
        setTokens(response.headers);
        setIsAuthenticated(true);
        setDeletedSession(response.deleted_session);
    }, []);
    // Clear auth state
    const clearAuthState = useCallback(() => {
        setUser(null);
        setTokens({ authorization: '', refresh: '' });
        setIsAuthenticated(false);
        setDeletedSession(undefined);
        setSharedUser(null);
        setIsSharedMode(false);
    }, []);
    // Load initial state from localStorage on mount
    useEffect(() => {
        const initializeAuth = async () => {
            const session = service.getSession();
            const sharedSession = service.getSharedSession();
            const { headers, userId } = session;
            const hasCredentials = !!userId && !!headers?.authorization && !!headers?.refresh;
            if (!hasCredentials) {
                if (sharedSession.sharedUserId || sharedSession.sharedTokens) {
                    service.removeSession();
                }
                setIsLoaded(true);
                return;
            }
            setTokens(headers);
            setIsAuthenticated(true);
            if (sharedSession.sharedUserId && sharedSession.sharedTokens) {
                setIsSharedMode(true);
                setSharedUser({
                    sharedUserId: sharedSession.sharedUserId,
                    sharedTokens: sharedSession.sharedTokens,
                });
                // Load shared user using shared tokens
                if (!user && (config.appPrefix === 'admin' || config.appPrefix === '')) {
                    await service.loadAndSetCurrentUser(sharedSession.sharedTokens, setUser);
                }
            }
            else {
                if (!user && (config.appPrefix === 'admin' || config.appPrefix === '')) {
                    await service.loadAndSetCurrentUser(headers, setUser);
                }
            }
            setIsLoaded(true);
        };
        void initializeAuth();
    }, [service]);
    // Context value
    const contextValue = useMemo(() => ({
        config,
        service,
        isAuthenticated,
        isLoaded,
        user,
        setUser,
        tokens,
        deletedSession,
        sharedUser,
        isSharedMode,
        updateAuthState,
        clearAuthState,
        setSharedUser,
        setIsSharedMode,
    }), [
        config,
        service,
        isAuthenticated,
        isLoaded,
        user,
        setUser,
        tokens,
        deletedSession,
        sharedUser,
        isSharedMode,
        updateAuthState,
        clearAuthState,
    ]);
    return (_jsx(AuthContext.Provider, { value: contextValue, children: children }));
};
//# sourceMappingURL=AuthProvider.js.map